import * as React from "react";
import {Slider} from "../slider/Slider";
import {LogarithmicSlider} from "../slider/LogarithmicSlider";
import type {PointDouble} from "../../../../../generated/api/base";
import {Functions} from "../../../../../utils/function/Functions";
import {Constants} from "../../../space/spaceeditor/logic3d/Constants";
import {EditableIcon} from "./EditableIcon";
import type {IEditableIconProps} from "./EditableIcon";

export type IconLayer = "Background" | "Image" | "Text";

interface IIconEditorProps extends IEditableIconProps {
	readonly selectedLayer: IconLayer;
	readonly isTextBeingModified: boolean;
	readonly onIsTextBeingModifiedChange: (value: boolean) => void;
	readonly onIconTranslateChange: (newTranslate: PointDouble) => void;
	readonly onIconOrientationChange: (newOrientation: number) => void;
	readonly onScaleChange: (newScale: number) => void;
	readonly onTextTranslateChange: (newTranslate: PointDouble) => void;
	readonly onTextOrientationChange: (newOrientation: number) => void;
	readonly onBorderRadiusChange: (newBorderRadius: number) => void;
	readonly svgRef: React.RefObject<SVGSVGElement>;
}

interface IIconEditorState {
	// we only update the other icons' properties on pointerup, otherwise it's slow
	scale: number;
	borderRadius: number;
}

export class IconEditor extends React.Component<IIconEditorProps, IIconEditorState> {
	public static readonly defaultProps: Partial<IIconEditorProps> = {
		selectedLayer: "Image",
		borderRadius: 5,
		backgroundColor: {hex: "FFFFFF", transparency: 1},
		iconColor: {hex: "FFFFFF", transparency: 1},
		replaceColor: false,
		text: "",
		textTranslate: {x: 0.5, y: 0.5},
		textOrientation: 0,
		onTextTranslateChange: Functions.emptyFunction,
		onTextOrientationChange: Functions.emptyFunction,
		onTextChange: Functions.emptyFunction,
		onBorderRadiusChange: Functions.emptyFunction,
		fontSize: 10,
		fontFamily: "Roboto",
		fontColor: {hex: "FFFFFF", transparency: 1},
		isBold: false,
		isItalic: false,
		isUnderlined: false,
		isTextBeingModified: false,
		onIsTextBeingModifiedChange: Functions.emptyFunction,
	};

	constructor(props: IIconEditorProps) {
		super(props);
		this.state = {
			scale: this.props.scale,
			borderRadius: this.props.borderRadius,
		};
	}

	private onScaleChange = (newScale: number) => {
		this.setState({
			scale: newScale,
		});
	};

	private onScaleEnd = () => {
		this.props.onScaleChange(this.state.scale);
	};

	private onBorderRadiusChange = (newBorderRadius: number) => {
		this.setState({
			borderRadius: newBorderRadius,
		});
	};

	private onBorderRadiusEnd = () => {
		this.props.onBorderRadiusChange(this.state.borderRadius);
	};

	private onTextChange = (newText: string) => {
		this.props.onTextChange(newText);
		this.props.onIsTextBeingModifiedChange(false);
	};

	public override render() {
		const {
			innerPart,
			imageAspectRatio,
			isSVG,
			selectedLayer,
			backgroundColor,
			iconColor,
			isIconColorChanged,
			iconColors,
			replaceColor,
			iconOrientation,
			iconTranslate,
			isBold,
			isFlippedX,
			isFlippedY,
			text,
			textOrientation,
			textTranslate,
			onIconOrientationChange,
			onIconTranslateChange,
			onIsTextBeingModifiedChange,
			onTextOrientationChange,
			onTextTranslateChange,
			fontColor,
			fontFamily,
			fontSize,
			isItalic,
			isTextBeingModified,
			isUnderlined,
			horizontalAlignment,
			svgRef,
			selectedChildIndex,
			onSelectedChildIndexChange,
		} = this.props;
		const {borderRadius, scale} = this.state;

		return (
			<>
				<EditableIcon
					innerPart={innerPart}
					imageAspectRatio={imageAspectRatio}
					isSVG={isSVG}
					classNames="IconEditor"
					backgroundColor={backgroundColor}
					isIconColorChanged={isIconColorChanged}
					iconColor={iconColor}
					iconColors={iconColors}
					selectedChildIndex={selectedChildIndex}
					onSelectedChildIndexChange={onSelectedChildIndexChange}
					replaceColor={replaceColor}
					borderRadius={borderRadius}
					iconTranslate={iconTranslate}
					iconOrientation={iconOrientation}
					scale={scale}
					isFlippedX={isFlippedX}
					isFlippedY={isFlippedY}
					text={text}
					textTranslate={textTranslate}
					textOrientation={textOrientation}
					onTextTranslateChange={onTextTranslateChange}
					onTextOrientationChange={onTextOrientationChange}
					onTextChange={this.onTextChange}
					onIsTextBeingModifiedChange={onIsTextBeingModifiedChange}
					onIconTranslateChange={onIconTranslateChange}
					onIconOrientationChange={onIconOrientationChange}
					selectedLayer={selectedLayer}
					fontColor={fontColor}
					fontSize={fontSize}
					fontFamily={fontFamily}
					isBold={isBold}
					isItalic={isItalic}
					isUnderlined={isUnderlined}
					horizontalAlignment={horizontalAlignment}
					isTextBeingModified={isTextBeingModified}
					svgRef={svgRef}
				/>
				{selectedLayer === "Image" && (
					<>
						<LogarithmicSlider
							width={150}
							value={scale}
							largeStepValue={0.25}
							min={0.25}
							max={4}
							classNames="iconEditorScale"
							title="Scale"
							onValueChange={this.onScaleChange}
							onPointerUp={this.onScaleEnd}
							arrows={true}
						/>
						<div className="center">Scale: {Math.round(scale * 100)}%</div>
					</>
				)}
				{selectedLayer === "Background" && (
					<>
						<Slider
							width={150}
							value={borderRadius}
							largeStepValue={1}
							min={0}
							max={Constants.CATALOG_SVG_VIEWBOXSIZE / 2}
							classNames="iconEditorScale"
							title="Border Radius"
							onValueChange={this.onBorderRadiusChange}
							onPointerUp={this.onBorderRadiusEnd}
							arrows={true}
						/>
						<div className="center">Border Radius: {Math.round((100 * borderRadius) / (Constants.CATALOG_SVG_VIEWBOXSIZE / 2))}%</div>
					</>
				)}
			</>
		);
	}
}
